<script setup>
import { ref, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useForm } from 'laravel-precognition-vue-inertia'
import { trans, transChoice } from 'laravel-vue-i18n'
import _ from 'lodash'
import AdditionalCostsWizardPage from './AdditionalCostsWizardPage.vue'
import FormWizardPage from './FormWizardPage.vue'
import RecapWizardPage from './RecapWizardPage.vue'
import NeoUiCalendar from '../ui-components/NeoUiCalendar.vue'
import NeoUiTimeslots from '../ui-components/NeoUiTimeslots.vue'
import { buildCalCells, sameDay, toYMD, getCalCellClass } from '../../composables/useRentalCalendar.js'

const props = defineProps({
    modelValue: Boolean,
    item: Object,
    forms: Object,
    initialDates: Object,
    initialGuests: Object,
    wizardTheme: Object,
})

const emit = defineEmits(['update:modelValue'])

// ── Form ───────────────────────────────────────────────────────────────────
const form = useForm('POST', props.forms.url, {
    name: '', phone: '', email: '', note: '',
    day: '', day_start: '', day_end: '', timeslot: '',
    id_reservation_locations: null, id_reservation_workers: null,
    id_services: null, id_rental_items: null,
    additional_costs: [], gdpr_consent: false, formdata: {},
    adults: 1, children: 0, pets: 0,
})

// ── Locale ─────────────────────────────────────────────────────────────────
const intlLocale = computed(() => (props.forms.locale ?? 'cs').replace('_', '-'))

const firstDayOfWeek = computed(() => {
    try {
        const info = new Intl.Locale(intlLocale.value).weekInfo
        if (info?.firstDay !== undefined) return info.firstDay
    } catch {}
    const sundayFirst = ['en', 'zh', 'ja', 'ko', 'ar', 'fa', 'he']
    return sundayFirst.includes(intlLocale.value.split('-')[0]) ? 0 : 1
})

const MONTHS = computed(() => {
    const fmt = new Intl.DateTimeFormat(intlLocale.value, { month: 'long' })
    return Array.from({ length: 12 }, (_, i) => {
        const s = fmt.format(new Date(2024, i, 1))
        return s.charAt(0).toUpperCase() + s.slice(1)
    })
})

const DOW = computed(() => {
    const fmt = new Intl.DateTimeFormat(intlLocale.value, { weekday: 'short' })
    return Array.from({ length: 7 }, (_, i) => {
        const dow = (firstDayOfWeek.value + i) % 7
        const s = fmt.format(new Date(2024, 0, 7 + dow))
        return s.charAt(0).toUpperCase() + s.slice(1)
    })
})

function fmtDateShort(d) {
    return d.toLocaleDateString(intlLocale.value, { day: 'numeric', month: 'short' })
}

function fmtKc(amount, currency = 'CZK') {
    return new Intl.NumberFormat(intlLocale.value, { style: 'currency', currency, minimumFractionDigits: 0 }).format(amount)
}

const UNIT_LABELS = {
    hour: trans('rental.unit_abbr_hour'),
    half_day: trans('rental.unit_abbr_half_day'),
    day: trans('rental.unit_abbr_day'),
    night: trans('rental.unit_abbr_night'),
    week: trans('rental.unit_abbr_week'),
    half_month: trans('rental.unit_abbr_half_month'),
    month: trans('rental.unit_abbr_month'),
    quarter: trans('rental.unit_abbr_quarter'),
    year: trans('rental.unit_abbr_year'),
    weekend: trans('rental.unit_abbr_weekend'),
    accommodation: trans('rental.unit_abbr_accommodation'),
}

// ── Item helpers ────────────────────────────────────────────────────────────
const isAccommodation = computed(() => props.forms.form_type?.name === 'accommodation')
const checkoutIsSingleDay = computed(() => ['hour', 'half_day'].includes(props.item?.unit))
const drawerMinUnits = computed(() => props.item?.min_unit_quantity ?? 1)
function itemBasePrice(item) { return item?.prices?.[0]?.unit_price ?? 0 }
function itemUnit(item) { return UNIT_LABELS[item?.unit] ?? (item?.unit ?? trans('rental.unit_abbr_night')) }

function itemPlaceholderColors(item) {
    const hue = (item?.id ?? 0) * 61 % 360
    return {
        c1: `oklch(0.70 0.12 ${hue})`,
        c2: `oklch(0.55 0.10 ${(hue + 30) % 360})`,
    }
}

// ── Blocking days (single item) ─────────────────────────────────────────────
const DAY_NAME_TO_DOW = {
    monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6, sunday: 0,
}

function blockedDaysOfType(type) {
    const item = props.item
    if (!item) return new Set()
    const blocked = new Set()
    for (let dow = 0; dow <= 6; dow++) {
        if (item.booking_rules?.some(r => r.blocking_type === type && DAY_NAME_TO_DOW[r.day] === dow)) {
            blocked.add(dow)
        }
    }
    return blocked
}

const blockedStartDays = computed(() => blockedDaysOfType('start'))
const blockedEndDays = computed(() => blockedDaysOfType('end'))
const blockedIntersectDays = computed(() => blockedDaysOfType('intersect'))

function rangeContainsBlockedIntersect(startDate, endDate) {
    if (!blockedIntersectDays.value.size) return false
    const days = Math.round((endDate - startDate) / 86400000)
    if (days >= 7) return true
    for (let i = 1; i < days; i++) {
        if (blockedIntersectDays.value.has(new Date(startDate.getTime() + i * 86400000).getDay())) return true
    }
    return false
}

// ── Calendar ────────────────────────────────────────────────────────────────
const selectedDates = ref(null)
const calView = ref({ y: new Date().getFullYear(), m: new Date().getMonth() })
const calHovered = ref(null)
const isRefreshingCalendar = ref(false)
const busyDaysData = ref({})
const drawerBodyRef = ref(null)

const calCells = computed(() => buildCalCells(calView.value.y, calView.value.m, firstDayOfWeek.value))

const nightsDiff = computed(() => {
    if (!selectedDates.value?.start || !selectedDates.value?.end) return 0
    return Math.round((new Date(selectedDates.value.end) - new Date(selectedDates.value.start)) / 86400000)
})

function isDrawerCellTooClose(cell) {
    if (drawerMinUnits.value <= 1) return false
    const s = selectedDates.value?.start ? new Date(selectedDates.value.start) : null
    if (!s || selectedDates.value?.end) return false
    if (cell.d <= s) return false
    return Math.round((cell.d - s) / 86400000) < drawerMinUnits.value
}

function calCellClass(cell) {
    const cls = getCalCellClass(cell, {
        hoveredDate: calHovered.value,
        selectedDates: selectedDates.value,
        blockedStartDays: blockedStartDays.value,
        blockedEndDays: blockedEndDays.value,
        busyDaysData: busyDaysData.value,
        rangeContainsBlockedIntersect,
    })
    if (isDrawerCellTooClose(cell)) { cls['blocked-end'] = true; cls['in-range'] = false }
    return cls
}

function calCellTooltip(cell) {
    if (isDrawerCellTooClose(cell)) return `Min. ${drawerMinUnits.value} × ${UNIT_LABELS[props.item?.unit] ?? trans('rental.unit_abbr_day')}`
    const today = new Date(); today.setHours(0, 0, 0, 0)
    if (cell.out || cell.d < today) return null
    const d = cell.d
    const s = selectedDates.value?.start ? new Date(selectedDates.value.start) : null
    const e = selectedDates.value?.end ? new Date(selectedDates.value.end) : null
    const isPickingStart = !s || (s && e)
    const isPickingEnd = !!(s && !e)
    if (isPickingStart && blockedStartDays.value.has(d.getDay())) return trans('widget.blocked_start_day')
    if (isPickingEnd && d > s) {
        if (blockedEndDays.value.has(d.getDay())) return trans('widget.blocked_end_day')
        if (rangeContainsBlockedIntersect(s, d)) return trans('widget.blocked_intersect_day')
    }
    return null
}

function calSetHover(cell) {
    if (cell.out) return
    const today = new Date(); today.setHours(0, 0, 0, 0)
    if (cell.d >= today) calHovered.value = cell.d
}

function calShift(n) {
    let m = calView.value.m + n, y = calView.value.y
    while (m < 0) { m += 12; y-- }
    while (m > 11) { m -= 12; y++ }
    calView.value = { y, m }
}

function handleCalPick(cell) {
    if (cell.out) return
    const today = new Date(); today.setHours(0, 0, 0, 0)
    if (cell.d < today) return
    if (isDrawerCellTooClose(cell)) return
    const d = cell.d
    const s = selectedDates.value?.start ? new Date(selectedDates.value.start) : null
    const e = selectedDates.value?.end ? new Date(selectedDates.value.end) : null
    if (!s || (s && e) || d < s) {
        if (blockedStartDays.value.has(d.getDay())) return
        selectedDates.value = { start: toYMD(d), end: null }
    } else if (d > s && (blockedEndDays.value.has(d.getDay()) || rangeContainsBlockedIntersect(s, d))) {
        return
    } else if (sameDay(d, s)) {
        const tomorrow = new Date(d); tomorrow.setDate(d.getDate() + 1)
        selectedDates.value = { start: toYMD(d), end: toYMD(tomorrow) }
    } else {
        selectedDates.value = { start: selectedDates.value.start, end: toYMD(d) }
    }
}

watch(selectedDates, (val) => {
    form.day_start = val?.start ?? ''
    form.day_end = val?.end ?? ''
})

function quickSelectNights(n) {
    const base = selectedDates.value?.start ? new Date(selectedDates.value.start) : new Date()
    base.setHours(0, 0, 0, 0)
    const end = new Date(base); end.setDate(base.getDate() + n)
    selectedDates.value = { start: toYMD(base), end: toYMD(end) }
}

// ── Busy days ───────────────────────────────────────────────────────────────
async function fetchBusyDays({ y, m }) {
    try {
        const start = `${y}-${String(m + 1).padStart(2, '0')}-01`
        const eY = m >= 11 ? y + 1 : y, eM = m >= 11 ? 0 : m + 1
        const lastDay = new Date(eY, eM + 1, 0).getDate()
        const end = `${eY}-${String(eM + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`
        const res = await axios.get(`/api/rental/busy-days/${props.forms.slug}/${start}/${end}`)
        busyDaysData.value = { ...busyDaysData.value, ...res.data }
    } catch {}
}

const fetchBusyDaysDebounced = _.debounce(fetchBusyDays, 300)

watch(calView, (val) => fetchBusyDaysDebounced(val))

async function refreshCalendar() {
    if (isRefreshingCalendar.value) return
    isRefreshingCalendar.value = true
    busyDaysData.value = {}
    try { await fetchBusyDays(calView.value) } finally { isRefreshingCalendar.value = false }
}

// ── Single-day mode ─────────────────────────────────────────────────────────
const drawerSingleDate = ref(null)
const drawerSlots = ref(null)
const drawerSelectedSlotId = ref(null)
const isLoadingSlots = ref(false)

const drawerSelectedSlot = computed(() =>
    drawerSlots.value?.slots?.find(s => String(s.id) === String(drawerSelectedSlotId.value)) ?? null
)

const drawerDateTimeRestrictions = computed(() => {
    const base = { min: new Date(), max: new Date(new Date().setFullYear(new Date().getFullYear() + 2)), disabled: [] }
    const slots = props.item?.time_slots
    if (!slots?.length) return base
    const vcWeekday = { sun: 1, mon: 2, tue: 3, wed: 4, thu: 5, fri: 6, sat: 7 }
    const daysWithSlots = new Set(slots.filter(vd => vd.time_slots?.length > 0).map(vd => vd.name))
    const disabledWeekdays = Object.keys(vcWeekday).filter(d => !daysWithSlots.has(d)).map(d => vcWeekday[d])
    return disabledWeekdays.length
        ? { ...base, disabled: [{ start: new Date(0), repeat: { weekdays: disabledWeekdays, until: base.max } }] }
        : base
})

watch(drawerSingleDate, async (date) => {
    if (!date || !props.item) return
    drawerSlots.value = null; drawerSelectedSlotId.value = null; isLoadingSlots.value = true
    try {
        const ymd = toYMD(date instanceof Date ? date : new Date(date))
        const res = await axios.get(`/api/rental/time-slots/${props.item.id}/${ymd}`)
        drawerSlots.value = res.data
    } catch { drawerSlots.value = { slots: [], all_taken: false } }
    finally { isLoadingSlots.value = false }
})

// ── Steps ───────────────────────────────────────────────────────────────────
const checkoutStep = ref(0)
const checkoutSteps = ref([])

const stepTitles = { dates: 'Vybrat termín', costs: 'Doplňkové služby', form: 'Vaše údaje', recap: 'Rekapitulace' }
const currentStepId = computed(() => checkoutSteps.value[checkoutStep.value] ?? 'form')

function buildCheckoutSteps(item) {
    const steps = []
    if (['hour', 'half_day'].includes(item.unit) || !selectedDates.value?.start || !selectedDates.value?.end) steps.push('dates')
    if (item.additional_costs?.length > 0) steps.push('costs')
    steps.push('form')
    steps.push('recap')
    return steps
}

watch(checkoutStep, () => nextTick(() => { if (drawerBodyRef.value) drawerBodyRef.value.scrollTop = 0 }))

// ── Price breakdown ─────────────────────────────────────────────────────────
const isTermTooShort = computed(() => {
    if (!props.item || !selectedDates.value?.start || !selectedDates.value?.end) return false
    const minUnits = drawerMinUnits.value
    if (!minUnits || minUnits <= 1) return false
    return Math.round((new Date(selectedDates.value.end) - new Date(selectedDates.value.start)) / 86400000) < minUnits
})

const drawerTotalLabel = computed(() => {
    if (!props.item) return null
    const base = itemBasePrice(props.item)
    const nights = nightsDiff.value
    let discountPct = 0, discountAmount = 0
    if (nights > 0) {
        const applicable = [...(props.item.discounts ?? [])]
            .filter(d => d.min_units <= nights)
            .sort((a, b) => b.min_units - a.min_units)[0]
        discountPct = applicable?.percentage_off ?? 0
        discountAmount = base * nights * (discountPct / 100)
    }
    const baseSubtotal = base * nights
    const rentalSubtotal = base * nights
    const guestCount = (form.adults ?? 1) + (form.children ?? 0)
    const nonOptionalAdditional = (props.item.additional_costs ?? [])
        .filter(c => !c.is_optional).reduce((s, c) => s + Number(c.unit_price), 0)
    const selectedOptionalAdditional = (form.additional_costs ?? []).reduce((sum, costId) => {
        const cost = props.item.additional_costs?.find(c => c.id === costId && c.is_optional)
        return sum + (cost ? Number(cost.unit_price) : 0)
    }, 0)
    const additionalTotal = nonOptionalAdditional + selectedOptionalAdditional
    const total = Math.round((nights > 0
        ? rentalSubtotal - discountAmount + additionalTotal
        : base + additionalTotal) * 100) / 100
    return { nights, base, baseSubtotal, hasPopRule: false, popRuleAmount: 0, guestCount, discountPct, discountAmount, rentalSubtotal, occupancyAdjustments: [], additionalTotal, total }
})

// ── handleDrawerNext ─────────────────────────────────────────────────────────
async function handleDrawerNext() {
    form.setErrors({})
    if (currentStepId.value === 'dates') {
        if (checkoutIsSingleDay.value) {
            if (!drawerSingleDate.value) { form.setErrors({ day_start: 'Vyberte prosím datum' }); return }
            if (!drawerSelectedSlot.value) { form.setErrors({ day_start: 'Vyberte prosím časový slot' }); return }
            const ymd = toYMD(drawerSingleDate.value instanceof Date ? drawerSingleDate.value : new Date(drawerSingleDate.value))
            form.day_start = `${ymd} ${drawerSelectedSlot.value.time_start}`
            form.day_end = `${ymd} ${drawerSelectedSlot.value.time_end}`
        } else {
            const today = new Date(); today.setHours(0, 0, 0, 0)
            if (!form.day_start || !form.day_end) { form.setErrors({ day_start: 'Vyberte prosím termín' }); return }
            if (new Date(form.day_start) < today) { form.setErrors({ day_start: 'Termín nesmí být v minulosti' }); return }
        }
        checkoutStep.value++
        return
    }
    if (currentStepId.value === 'form') {
        const validateFields = ['name', 'email', 'phone', 'id_reservation_locations']
        try {
            await axios.post(props.forms.url, form.data(), {
                headers: { 'Precognition': 'true', 'Precognition-Validate-Only': validateFields.join(',') },
            })
            checkoutStep.value++
        } catch (error) { form.setErrors(error.response?.data?.errors ?? {}) }
        return
    }
    checkoutStep.value++
}

// ── Reset on open ────────────────────────────────────────────────────────────
watch(() => props.modelValue, (visible) => {
    if (!visible) return
    form.setErrors({})
    form.id_rental_items = props.item?.id ?? null
    form.day_start = props.initialDates?.start ?? ''
    form.day_end = props.initialDates?.end ?? ''
    form.adults = props.initialGuests?.adults ?? 1
    form.children = props.initialGuests?.children ?? 0
    form.pets = props.initialGuests?.pets ?? 0
    form.name = ''; form.phone = ''; form.email = ''; form.note = ''
    form.gdpr_consent = false; form.formdata = {}; form.id_reservation_locations = null
    form.additional_costs = props.item?.additional_costs?.filter(c => !c.is_optional)?.map(c => c.id) ?? []
    selectedDates.value = props.initialDates ? { ...props.initialDates } : null
    checkoutStep.value = 0
    checkoutSteps.value = buildCheckoutSteps(props.item ?? { unit: 'night' })
    calHovered.value = null
    drawerSingleDate.value = checkoutIsSingleDay.value ? new Date() : null
    drawerSlots.value = null; drawerSelectedSlotId.value = null
    fetchBusyDays(calView.value)
    nextTick(() => { if (drawerBodyRef.value) drawerBodyRef.value.scrollTop = 0 })
})

// ── Keyboard ────────────────────────────────────────────────────────────────
function onKeyDown(e) {
    if (e.key === 'Escape' && props.modelValue) emit('update:modelValue', false)
}
onMounted(() => document.addEventListener('keydown', onKeyDown))
onUnmounted(() => document.removeEventListener('keydown', onKeyDown))
</script>

<template>
    <div class="rw-scrim" :class="{'is-open': modelValue}" @click="emit('update:modelValue', false)"/>
    <div class="rw-drawer" :class="{'is-open': modelValue}" role="dialog" aria-modal="true">
        <!-- Drawer header -->
        <div class="rw-drawer-hd">
            <div class="rw-drawer-step-info">
                <template v-if="item && checkoutSteps.length">
                    Krok <b>{{ checkoutStep + 1 }}</b> ze <b>{{ checkoutSteps.length }}</b>
                    &nbsp;·&nbsp; {{ stepTitles[currentStepId] }}
                </template>
            </div>
            <button class="rw-drawer-close" @click="emit('update:modelValue', false)" aria-label="Zavřít">
                <svg class="rw-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                    <path d="M6 6l12 12M6 18 18 6"/>
                </svg>
            </button>
        </div>

        <!-- Drawer body -->
        <div v-if="item" class="rw-drawer-body" ref="drawerBodyRef">
            <!-- Mini listing card -->
            <div class="rw-d-listing">
                <div class="rw-d-listing-photo">
                    <img v-if="item.gallery?.[0]?.full_url" :src="item.gallery[0].full_url" :alt="item.name"/>
                    <svg v-else viewBox="0 0 400 300" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg" style="display:block;width:100%;height:100%">
                        <defs>
                            <linearGradient :id="`rw-dg-${item.id}`" x1="0" y1="0" x2="1" y2="1">
                                <stop offset="0" :stop-color="itemPlaceholderColors(item).c1"/>
                                <stop offset="1" :stop-color="itemPlaceholderColors(item).c2"/>
                            </linearGradient>
                        </defs>
                        <rect width="400" height="300" :fill="`url(#rw-dg-${item.id})`"/>
                    </svg>
                </div>
                <div class="rw-d-listing-meta">
                    <div class="rw-d-listing-type">{{ isAccommodation ? 'Ubytování' : 'Pronájem' }}</div>
                    <h3>{{ item.name }}</h3>
                    <div class="rw-d-listing-price">
                        <b>{{ fmtKc(itemBasePrice(item)) }}</b> / {{ itemUnit(item) }}
                    </div>
                </div>
            </div>

            <!-- Step progress -->
            <div class="rw-steps">
                <div v-for="(step, i) in checkoutSteps" :key="step" class="rw-step" :class="{done: i < checkoutStep, now: i === checkoutStep}">
                    <div class="rw-step-ix">{{ String(i + 1).padStart(2, '0') }}</div>
                    <div class="rw-step-nm">{{ stepTitles[step] }}</div>
                    <div class="rw-step-bar"/>
                </div>
            </div>

            <!-- Step: Date selection -->
            <div v-if="currentStepId === 'dates'" class="rw-fset">
                <h4>{{ $t('widget.select_date_label') }}</h4>

                <!-- Month nav (only multi-day) -->
                <div v-if="!checkoutIsSingleDay" class="rw-cal-hd">
                    <div class="rw-cal-nav">
                        <button @click="calShift(-1)" aria-label="Předchozí">
                            <svg class="rw-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 6-6 6 6 6"/></svg>
                        </button>
                    </div>
                    <b>{{ MONTHS[calView.m] }} {{ calView.y }}</b>
                    <div class="rw-cal-nav">
                        <button class="rw-cal-nav-btn--refresh" :disabled="isRefreshingCalendar" @click="refreshCalendar" :title="$t('widget.refresh_calendar')">
                            <svg class="rw-icon" :class="{'rw-spin': isRefreshingCalendar}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/><path d="M8 16H3v5"/></svg>
                        </button>
                        <button @click="calShift(1)" aria-label="Další">
                            <svg class="rw-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 6 6 6-6 6"/></svg>
                        </button>
                    </div>
                </div>

                <!-- Single-day mode -->
                <template v-if="checkoutIsSingleDay">
                    <neo-ui-calendar
                        v-model="drawerSingleDate"
                        :date-time-restrictions="drawerDateTimeRestrictions"
                        :calendar-dates="[]"
                        :theme="wizardTheme"
                        :locale="forms.locale"
                        :settings="forms.settings"
                        :neo-design="true"
                    />
                    <neo-ui-timeslots
                        v-model="drawerSelectedSlotId"
                        :available-time-slots="isLoadingSlots ? null : drawerSlots"
                        :selected-date="drawerSingleDate"
                        :theme="wizardTheme"
                    />
                </template>

                <!-- Multi-day range calendar -->
                <template v-else>
                    <div class="rw-cal-grid" :class="{'rw-cal-grid--refreshing': isRefreshingCalendar}" @mouseleave="calHovered = null">
                        <div v-for="d in DOW" :key="d" class="rw-cal-dow">{{ d }}</div>
                        <button
                            v-for="(cell, i) in calCells"
                            :key="i"
                            :class="calCellClass(cell)"
                            @click="handleCalPick(cell)"
                            @mouseenter="calSetHover(cell)"
                        >{{ cell.d.getDate() }}<span v-if="calCellTooltip(cell)" class="rw-cell-tip">{{ calCellTooltip(cell) }}</span></button>
                    </div>

                    <div class="rw-drawer-dates-summary">
                        <div style="display:flex;gap:20px">
                            <div>
                                <div class="rw-dates-label">{{ isAccommodation ? trans('widget.arrival') : trans('widget.date_start') }}</div>
                                <div class="rw-dates-val">{{ selectedDates?.start ? fmtDateShort(new Date(selectedDates.start)) : '—' }}</div>
                            </div>
                            <div style="width:1px;background:var(--rw-line-2)"/>
                            <div>
                                <div class="rw-dates-label">{{ isAccommodation ? trans('widget.departure') : trans('widget.date_end') }}</div>
                                <div class="rw-dates-val">{{ selectedDates?.end ? fmtDateShort(new Date(selectedDates.end)) : '—' }}</div>
                            </div>
                        </div>
                        <div class="rw-dates-nights">
                            <template v-if="nightsDiff > 0">
                                {{ isAccommodation ? transChoice('widget.nights', nightsDiff, {count: nightsDiff}) : transChoice('widget.days_count', nightsDiff, {count: nightsDiff}) }}
                            </template>
                            <template v-else>{{ trans('widget.select_range') }}</template>
                        </div>
                    </div>

                    <div class="rw-night-chips">
                        <button v-for="n in [2, 3, 7]" :key="n" class="rw-chip" @click="quickSelectNights(n)">
                            {{ isAccommodation ? transChoice('widget.nights', n, {count: n}) : transChoice('widget.days_count', n, {count: n}) }}
                        </button>
                        <button class="rw-chip" @click="selectedDates = null">{{ trans('widget.clear') }}</button>
                    </div>

                    <!-- Guest selector for accommodation -->
                    <template v-if="isAccommodation">
                        <div class="rw-guests">
                            <div class="rw-guest-row">
                                <div>
                                    <div class="rw-guest-lbl">{{ $t('common.adults') }}</div>
                                    <div class="rw-guest-sub">{{ $t('widget.adults_age_range') }}</div>
                                </div>
                                <div class="rw-counter">
                                    <button @click="form.adults = Math.max(1, form.adults - 1)" :disabled="form.adults <= 1">−</button>
                                    <span>{{ form.adults }}</span>
                                    <button @click="form.adults++"
                                        :disabled="(item?.max_adults != null && form.adults >= item.max_adults)
                                            || (item?.max_pop != null && (form.adults + form.children) >= item.max_pop)">+</button>
                                </div>
                            </div>
                            <div class="rw-guest-row">
                                <div>
                                    <div class="rw-guest-lbl">{{ $t('common.children') }}</div>
                                    <div class="rw-guest-sub">{{ $t('widget.children_age_range') }}</div>
                                </div>
                                <div class="rw-counter">
                                    <button @click="form.children = Math.max(0, form.children - 1)" :disabled="form.children <= 0">−</button>
                                    <span>{{ form.children }}</span>
                                    <button @click="form.children++"
                                        :disabled="(item?.max_children != null && form.children >= item.max_children)
                                            || (item?.max_pop != null && (form.adults + form.children) >= item.max_pop)">+</button>
                                </div>
                            </div>
                            <div v-if="item?.max_pets !== 0" class="rw-guest-row">
                                <div>
                                    <div class="rw-guest-lbl">{{ $t('common.pets') }}</div>
                                </div>
                                <div class="rw-counter">
                                    <button @click="form.pets = Math.max(0, form.pets - 1)" :disabled="form.pets <= 0">−</button>
                                    <span>{{ form.pets }}</span>
                                    <button @click="form.pets++" :disabled="item?.max_pets != null && form.pets >= item.max_pets">+</button>
                                </div>
                            </div>
                            <small v-if="item?.max_pop" class="rw-guest-cap">{{ $t('widget.capacity_max_short', { count: item.max_pop }) }}</small>
                        </div>
                    </template>
                </template>

                <small v-if="form.errors?.day_start" class="rw-error">{{ form.errors.day_start }}</small>
            </div>

            <!-- Step: Additional costs -->
            <additional-costs-wizard-page
                v-if="currentStepId === 'costs'"
                v-model:form="form"
                :items="[item]"
                :theme="wizardTheme"
                roundness=""
            />

            <!-- Step: Personal info -->
            <form-wizard-page
                v-if="currentStepId === 'form'"
                v-model:form="form"
                :locations="forms.reservation_locations"
                :theme="wizardTheme"
                :current-brightness="150"
                :neo-design="true"
            />

            <!-- Step: Recap + submit -->
            <recap-wizard-page
                v-if="currentStepId === 'recap'"
                v-model:form="form"
                v-model:current-slide="checkoutStep"
                :locations="forms.reservation_locations"
                :theme="wizardTheme"
                :current-brightness="150"
                roundness=""
                :neo-design="true"
                :price-breakdown="drawerTotalLabel"
            />
        </div>

        <!-- Drawer footer -->
        <div v-if="currentStepId !== 'recap'" class="rw-drawer-fd">
            <div class="rw-drawer-totals">
                <template v-if="drawerTotalLabel && drawerTotalLabel.nights > 0">
                    <div class="rw-totals-breakdown">
                        <div class="rw-totals-row">
                            <span>{{ drawerTotalLabel.nights }} × {{ fmtKc(drawerTotalLabel.base) }}</span>
                            <span>{{ fmtKc(drawerTotalLabel.baseSubtotal) }}</span>
                        </div>
                        <div v-if="drawerTotalLabel.discountPct > 0" class="rw-totals-row rw-totals-row--discount">
                            <span>{{ $t('widget.discount_pct', { pct: drawerTotalLabel.discountPct }) }}</span>
                            <span>−{{ fmtKc(drawerTotalLabel.discountAmount) }}</span>
                        </div>
                        <div v-if="drawerTotalLabel.additionalTotal > 0" class="rw-totals-row">
                            <span>{{ $t('widget.additional_costs') }}</span>
                            <span>+{{ fmtKc(drawerTotalLabel.additionalTotal) }}</span>
                        </div>
                        <div class="rw-totals-total-row">
                            <span>{{ $t('widget.bill_total') }}</span>
                            <span>{{ fmtKc(drawerTotalLabel.total) }}</span>
                        </div>
                    </div>
                </template>
                <template v-else-if="item">
                    <div class="rw-totals-lbl">{{ $t('widget.per_unit', { unit: itemUnit(item) }) }}</div>
                    <div class="rw-totals-amt">{{ fmtKc(itemBasePrice(item)) }}</div>
                </template>
            </div>
            <div class="rw-drawer-fd-btns">
                <button v-if="checkoutStep > 0" class="rw-btn rw-btn-secondary" @click="checkoutStep--">{{ $t('common.back') }}</button>
                <div class="rw-btn-wrap">
                    <button
                        class="rw-btn rw-btn-primary"
                        :disabled="currentStepId === 'dates' && !checkoutIsSingleDay && isTermTooShort"
                        @click="handleDrawerNext"
                    >
                        {{ $t('common.continue') }}
                        <svg class="rw-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14M13 6l6 6-6 6"/></svg>
                    </button>
                    <small v-if="currentStepId === 'dates' && !checkoutIsSingleDay && isTermTooShort" class="rw-unavail-hint">
                        {{ $t('widget.min_duration_display') }}: {{ drawerMinUnits }} × {{ UNIT_LABELS[item?.unit] ?? $t('rental.unit_abbr_day') }}
                    </small>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* ─── Scrim & Drawer shell ────────────────────────────────────────────────── */
.rw-scrim {
    position: fixed; inset: 0;
    background: rgba(15, 17, 21, 0.4); backdrop-filter: blur(2px);
    z-index: 90; opacity: 0; pointer-events: none; transition: opacity 0.25s;
}
.rw-scrim.is-open { opacity: 1; pointer-events: auto; }

.rw-drawer {
    position: fixed; top: 0; right: 0; bottom: 0; width: 520px; max-width: 96vw;
    background: var(--rw-panel-bg, var(--rw-paper));
    backdrop-filter: blur(var(--rw-panel-blur, 0px));
    -webkit-backdrop-filter: blur(var(--rw-panel-blur, 0px));
    z-index: 91; display: flex; flex-direction: column;
    transform: translateX(100%);
    transition: transform 0.35s cubic-bezier(0.25, 1, 0.3, 1);
}
.rw-drawer.is-open {
    transform: translateX(0);
    box-shadow: -30px 0 80px -20px rgba(15, 17, 21, 0.35);
}

.rw-drawer-hd {
    display: flex; align-items: center; justify-content: space-between;
    padding: 18px 24px; border-bottom: 1px solid var(--rw-line-2);
    position: sticky; top: 0;
    background: var(--rw-panel-bg, var(--rw-paper));
    backdrop-filter: blur(var(--rw-panel-blur, 0px));
    -webkit-backdrop-filter: blur(var(--rw-panel-blur, 0px));
    z-index: 2; flex-shrink: 0;
}
.rw-drawer-step-info {
    font-family: var(--rw-font-mono); font-size: 11px; color: var(--rw-ink-3);
    letter-spacing: 0.04em; text-transform: uppercase;
}
.rw-drawer-step-info b { color: var(--rw-ink); font-weight: 600; }
.rw-drawer-close {
    width: 36px; height: 36px; border-radius: 999px; display: flex; align-items: center; justify-content: center;
    color: var(--rw-ink-2); border: 1px solid var(--rw-line); background: none; cursor: pointer; font-family: inherit; flex-shrink: 0;
}
.rw-drawer-close:hover { background: var(--rw-bg); color: var(--rw-ink); }
.rw-drawer-body { flex: 1; overflow-y: auto; padding: 24px 28px; }

/* ─── Mini listing card ───────────────────────────────────────────────────── */
.rw-d-listing { display: flex; gap: 14px; align-items: center; padding-bottom: 18px; border-bottom: 1px solid var(--rw-line-2); margin-bottom: 22px; }
.rw-d-listing-photo { width: 84px; height: 84px; border-radius: 14px; overflow: hidden; flex-shrink: 0; background: var(--rw-line-2); }
.rw-d-listing-photo img { width: 100%; height: 100%; object-fit: cover; display: block; }
.rw-d-listing-meta { flex: 1; min-width: 0; }
.rw-d-listing-type { font-family: var(--rw-font-mono); font-size: 11px; color: var(--rw-ink-3); text-transform: uppercase; letter-spacing: 0.04em; }
.rw-d-listing-meta h3 { font-family: var(--rw-font-display); font-size: 17px; font-weight: 600; margin: 3px 0 0; letter-spacing: -0.01em; }
.rw-d-listing-price { font-size: 13px; color: var(--rw-ink-2); margin-top: 6px; }
.rw-d-listing-price b { font-family: var(--rw-font-display); color: var(--rw-ink); font-weight: 600; }

/* ─── Steps ───────────────────────────────────────────────────────────────── */
.rw-steps { display: flex; align-items: center; gap: 6px; padding-bottom: 20px; }
.rw-step { flex: 1; display: flex; flex-direction: column; gap: 6px; padding: 6px 0; }
.rw-step-ix { font-family: var(--rw-font-mono); font-size: 10px; color: var(--rw-ink-4); font-weight: 600; letter-spacing: 0.04em; }
.rw-step-nm { font-size: 12px; color: var(--rw-ink-3); font-weight: 500; }
.rw-step-bar { height: 3px; background: var(--rw-line); border-radius: 999px; width: 100%; margin-top: auto; }
.rw-step.done .rw-step-bar, .rw-step.now .rw-step-bar { background: var(--brand); }
.rw-step.done .rw-step-ix, .rw-step.now .rw-step-ix { color: var(--brand); }
.rw-step.now .rw-step-nm, .rw-step.done .rw-step-nm { color: var(--rw-ink); }

/* ─── Fset ────────────────────────────────────────────────────────────────── */
.rw-fset { display: flex; flex-direction: column; gap: 18px; }
.rw-fset h4 { font-family: var(--rw-font-display); font-size: 16px; font-weight: 600; letter-spacing: -0.01em; margin: 0; }

/* ─── Calendar ────────────────────────────────────────────────────────────── */
.rw-cal-hd { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.rw-cal-hd b { font-size: 14px; font-weight: 600; }
.rw-cal-nav { display: flex; gap: 6px; }
.rw-cal-nav button {
    width: 30px; height: 30px; border-radius: 999px; display: flex; align-items: center; justify-content: center;
    border: 1px solid var(--rw-line); color: var(--rw-ink-2); background: none; cursor: pointer; font-family: inherit;
}
.rw-cal-nav button:hover { border-color: var(--rw-ink-3); color: var(--rw-ink); }
.rw-cal-nav-btn--refresh {
    width: 30px; height: 30px; border-radius: 999px; display: flex; align-items: center; justify-content: center;
    border: 1px solid var(--rw-line); color: var(--rw-ink-3); background: none; cursor: pointer; font-family: inherit; flex-shrink: 0;
}
.rw-cal-nav-btn--refresh:hover:not(:disabled) { border-color: var(--rw-ink-3); color: var(--rw-ink); }
.rw-cal-nav-btn--refresh:disabled { opacity: 0.4; cursor: default; }

@keyframes rw-spin { to { transform: rotate(360deg); } }
.rw-spin { animation: rw-spin 0.7s linear infinite; display: inline-block; }

.rw-cal-grid--refreshing { opacity: 0.4; pointer-events: none; transition: opacity 0.15s; }
.rw-cal-grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 2px; }
.rw-cal-dow { font-size: 10px; font-family: var(--rw-font-mono); color: var(--rw-ink-3); text-align: center; padding: 6px 0; font-weight: 500; letter-spacing: 0.04em; }
.rw-cal-cell {
    height: 38px; display: flex; align-items: center; justify-content: center; font-size: 13px;
    border-radius: var(--rw-r-sm); color: var(--rw-ink-2); position: relative;
    border: none; background: none; cursor: pointer; font-family: inherit; padding: 0;
}
.rw-cal-cell.out { color: var(--rw-ink-4); opacity: 0.4; pointer-events: none; }
.rw-cal-cell.out.in-range { opacity: 0.25; background: var(--rw-brand-tint); border-radius: 0; }
.rw-cal-cell.disabled { color: var(--rw-ink-4); text-decoration: line-through; opacity: 0.5; pointer-events: none; }
.rw-cal-cell.blocked-start,
.rw-cal-cell.blocked-end {
    color: var(--rw-ink-4); opacity: 0.4; cursor: not-allowed;
    background: repeating-linear-gradient(-45deg, transparent, transparent 3px, rgba(0,0,0,0.06) 3px, rgba(0,0,0,0.06) 6px);
}
.dark .rw-cal-cell.blocked-start, .dark .rw-cal-cell.blocked-end {
    background: repeating-linear-gradient(-45deg, transparent, transparent 3px, rgba(255,255,255,0.06) 3px, rgba(255,255,255,0.06) 6px);
}
.rw-cell-tip {
    position: absolute; bottom: calc(100% + 6px); left: 50%; transform: translateX(-50%);
    background: #1a1a1a; color: #fff; font-size: 11px; line-height: 1.4; white-space: nowrap;
    padding: 4px 8px; border-radius: 5px; pointer-events: none; z-index: 200; display: none; box-shadow: 0 2px 8px rgba(0,0,0,0.25);
}
.rw-cell-tip::after { content: ''; position: absolute; top: 100%; left: 50%; transform: translateX(-50%); border: 4px solid transparent; border-top-color: #1a1a1a; }
.rw-cal-cell:hover .rw-cell-tip { display: block; }
.rw-cal-cell:not(.out):not(.disabled):not(.blocked-start):not(.blocked-end):hover { background: var(--rw-bg); }
.rw-cal-cell.in-range { background: var(--rw-brand-tint); border-radius: 0; }
.rw-cal-cell.range-start { background: var(--brand); color: #fff; border-radius: var(--rw-r-sm) 0 0 var(--rw-r-sm); font-weight: 600; }
.rw-cal-cell.range-end { background: var(--brand); color: #fff; border-radius: 0 var(--rw-r-sm) var(--rw-r-sm) 0; font-weight: 600; }
.rw-cal-cell.range-single { background: var(--brand); color: #fff; border-radius: var(--rw-r-sm); font-weight: 600; }
.rw-cal-cell.today::after { content: ""; position: absolute; bottom: 5px; left: 50%; transform: translateX(-50%); width: 3px; height: 3px; border-radius: 999px; background: currentColor; }
.rw-cal-cell.busy-full::before { content: ""; position: absolute; bottom: 5px; left: 50%; transform: translateX(-50%); width: 4px; height: 4px; border-radius: 999px; background: #ef4444; }
.rw-cal-cell.busy-partial::before { content: ""; position: absolute; bottom: 5px; left: 50%; transform: translateX(-50%); width: 4px; height: 4px; border-radius: 999px; background: #f59e0b; }

/* ─── Dates summary + chips ───────────────────────────────────────────────── */
.rw-drawer-dates-summary { display: flex; align-items: center; justify-content: space-between; padding: 10px 0; border-top: 1px solid var(--rw-line-2); }
.rw-dates-label { font-family: var(--rw-font-mono); font-size: 11px; color: var(--rw-ink-3); text-transform: uppercase; letter-spacing: 0.04em; font-weight: 600; }
.rw-dates-val { font-size: 15px; font-weight: 600; margin-top: 2px; }
.rw-dates-nights { font-size: 13px; color: var(--rw-ink-3); }
.rw-night-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.rw-error { color: #ef4444; font-size: 13px; }

/* ─── Guest selector ─────────────────────────────────────────────────────── */
.rw-guests { display: flex; flex-direction: column; gap: 14px; padding: 4px 0 8px; }
.rw-guest-row { display: flex; justify-content: space-between; align-items: center; }
.rw-guest-lbl { font-size: 14px; font-weight: 500; color: var(--rw-ink); }
.rw-guest-sub { font-size: 12px; color: var(--rw-ink-3); margin-top: 2px; }
.rw-counter { display: flex; align-items: center; gap: 14px; }
.rw-counter button {
    width: 32px; height: 32px; border-radius: 50%; border: 1.5px solid var(--rw-line); background: var(--rw-paper);
    font-size: 18px; line-height: 1; cursor: pointer; color: var(--rw-ink); display: flex; align-items: center; justify-content: center; transition: opacity 0.12s;
}
.rw-counter button:disabled { opacity: 0.3; cursor: default; }
.rw-counter span { font-size: 16px; font-weight: 600; min-width: 20px; text-align: center; color: var(--rw-ink); }
.rw-guest-cap { color: var(--rw-ink-3); font-size: 12px; margin-top: -4px; }

/* ─── Drawer footer ───────────────────────────────────────────────────────── */
.rw-drawer-fd {
    border-top: 1px solid var(--rw-line-2); padding: 16px 24px;
    background: var(--rw-panel-bg, var(--rw-bg-2));
    backdrop-filter: blur(var(--rw-panel-blur, 0px));
    -webkit-backdrop-filter: blur(var(--rw-panel-blur, 0px));
    display: flex; align-items: flex-end; justify-content: space-between; gap: 16px; flex-shrink: 0;
}
.rw-drawer-totals { flex: 1; min-width: 0; }
.rw-totals-breakdown { display: flex; flex-direction: column; gap: 3px; }
.rw-totals-row { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; font-size: 12px; color: var(--rw-ink-3); font-family: var(--rw-font-body); }
.rw-totals-row > span:last-child { white-space: nowrap; font-variant-numeric: tabular-nums; }
.rw-totals-row--discount > span:last-child { color: #16a34a; }
.rw-totals-total-row { display: flex; justify-content: space-between; align-items: baseline; gap: 8px; margin-top: 7px; padding-top: 7px; border-top: 1px solid var(--rw-line-2); }
.rw-totals-total-row > span:first-child { font-size: 11px; color: var(--rw-ink-3); font-family: var(--rw-font-mono); letter-spacing: 0.04em; text-transform: uppercase; font-weight: 600; }
.rw-totals-total-row > span:last-child { font-family: var(--rw-font-display); font-size: 20px; font-weight: 600; letter-spacing: -0.015em; color: var(--rw-ink); white-space: nowrap; font-variant-numeric: tabular-nums; }
.rw-totals-lbl { font-size: 11px; color: var(--rw-ink-3); font-family: var(--rw-font-mono); letter-spacing: 0.04em; text-transform: uppercase; font-weight: 600; }
.rw-totals-amt { font-family: var(--rw-font-display); font-size: 22px; font-weight: 600; letter-spacing: -0.015em; margin-top: 2px; }

.rw-drawer-fd-btns { display: flex; gap: 8px; align-items: flex-start; }
.rw-btn-wrap { display: flex; flex-direction: column; gap: 5px; flex: 1; }
.rw-unavail-hint { font-size: 11px; color: #ef4444; text-align: center; }

/* ─── Buttons ─────────────────────────────────────────────────────────────── */
.rw-btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 8px;
    padding: 0 18px; height: 46px; border-radius: var(--rw-r-md);
    font-weight: 600; font-size: 14px; cursor: pointer; transition: background 0.12s, border-color 0.12s;
    font-family: inherit; border: none;
}
.rw-btn-primary { background: var(--brand); color: #fff; }
.rw-btn-primary:hover:not(:disabled) { background: var(--rw-brand-2); }
.rw-btn-primary:disabled { opacity: 0.4; cursor: not-allowed; }
.rw-btn-secondary { background: var(--rw-paper); border: 1px solid var(--rw-line); color: var(--rw-ink); }
.rw-btn-secondary:hover { border-color: var(--rw-ink-3); }

/* ─── Icon ────────────────────────────────────────────────────────────────── */
.rw-icon { width: 16px; height: 16px; flex-shrink: 0; }

/* ─── Responsive ─────────────────────────────────────────────────────────── */
@media (max-width: 768px) {
    .rw-drawer { width: 100%; max-width: 100%; border-radius: 0; }
    .rw-drawer-body { padding: 18px; }
    .rw-drawer-fd { padding: 14px 18px 18px; flex-wrap: wrap; gap: 10px; align-items: flex-end; }
    .rw-totals-amt { font-size: 18px; }
    .rw-totals-total-row > span:last-child { font-size: 18px; }
    .rw-drawer-fd-btns { flex: 1; justify-content: flex-end; }
    .rw-btn { height: 44px; font-size: 13px; padding: 0 14px; }
    .rw-d-listing-photo { width: 64px; height: 64px; }
    .rw-d-listing-meta h3 { font-size: 15px; }
}
</style>
